// src/screens/CreateGame/CreateGame.js
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { loadStadiums } from '../../store/slices/stadiumSlice';
import { resetPhase } from '../../store/slices/gamePhaseSlice';
import { setSelectedStadium } from '../../store/slices/stadiumSelectionSlice';
import { setSelectedDate } from '../../store/slices/dateSelectionSlice';
import { setSelectedSlot } from '../../store/slices/slotSelectionSlice';
import { setSelectedLevel } from '../../store/slices/levelSelectionSlice';
import { setSelectedHost } from '../../store/slices/hostSelectionSlice';
import './CreateGame.css';
import StadiumSelection from "../../components/GameCreation/StadiumSelection";
import DateSelection from "../../components/GameCreation/DateSelection";
import SlotSelection from "../../components/GameCreation/SlotSelection";
import LevelSelection from "../../components/GameCreation/LevelSelection";
import HostSelection from "../../components/GameCreation/HostSelection";
import Confirmation from "../../components/GameCreation/Confirmation";
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import CreateGameImageTwo from '../../assets/images/turia.png';
import { toCalendarDateString } from '../../utils/calendarDate';
import { clearStoredAdminSession, runAdminAuthedRequest } from '../../utils/adminAuth';

const addMinutesToTime = (timeValue, minutesToAdd) => {
  const [hoursRaw, minutesRaw] = String(timeValue || '').split(':').map(Number);
  const minutesInt = Number(minutesToAdd);
  if (!Number.isFinite(hoursRaw) || !Number.isFinite(minutesRaw) || !Number.isFinite(minutesInt)) {
    return null;
  }

  const totalMinutesInDay = 24 * 60;
  const totalMinutes = ((hoursRaw * 60 + minutesRaw + minutesInt) % totalMinutesInDay + totalMinutesInDay) % totalMinutesInDay;
  const outHours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const outMinutes = String(totalMinutes % 60).padStart(2, '0');
  return `${outHours}:${outMinutes}`;
};

const findStadiumForGame = (game, stadiumList) => {
  const byId = stadiumList.find((stadium) => {
    if (!game?.stadium?.stadiumId) return false;
    return String(stadium._id) === String(game.stadium.stadiumId);
  });
  if (byId) return byId;

  const gameName = String(game?.stadium?.name || '').trim().toLowerCase();
  const gameAddress = String(game?.stadium?.address || '').trim().toLowerCase();
  return stadiumList.find((stadium) => {
    const stadiumName = String(stadium?.name || '').trim().toLowerCase();
    const stadiumAddress = String(stadium?.address || '').trim().toLowerCase();
    return stadiumName === gameName && stadiumAddress === gameAddress;
  }) || null;
};

const findSlotForGame = (game, stadium) => {
  const slots = Array.isArray(stadium?.slots) ? stadium.slots : [];
  if (!slots.length) return null;

  const expectedEnd = addMinutesToTime(game?.time, Number(game?.duration || 0));

  const byExact = slots.find(
    (slot) => slot?.startTime === game?.time && (!expectedEnd || slot?.endTime === expectedEnd)
  );
  if (byExact) return byExact;

  const byStart = slots.find((slot) => slot?.startTime === game?.time);
  return byStart || slots[0];
};

const findHostForGame = (game, stadium) => {
  const hostId = game?.host?._id ? String(game.host._id) : null;
  if (!hostId) return null;

  const stadiumHosts = Array.isArray(stadium?.hosts) ? stadium.hosts : [];
  const fromStadium = stadiumHosts.find((host) => String(host?._id) === hostId);
  if (fromStadium) return fromStadium;

  if (game?.host) {
    return {
      _id: game.host._id,
      email: game.host.email,
      first_name: game.host.first_name,
      last_name: game.host.last_name,
      phone_number: game.host.phone_number,
      description: game.host.description,
      hostPicture: game.host.hostPicture,
    };
  }

  return null;
};

function CreateGame({ mode = 'create' }) {
  const { gameId } = useParams();
  const dispatch = useDispatch();
  const stadiums = useSelector((state) => state.stadiums.list);
  const stadiumsStatus = useSelector((state) => state.stadiums.status);
  const phase = useSelector((state) => state.gamePhase.phase);
  const selectedStadium = useSelector((state) => state.stadiumSelection.selectedStadium);
  const selectedDate = useSelector((state) => state.dateSelection.selectedDate);
  const selectedSlot = useSelector((state) => state.slotSelection.selectedSlot);
  const selectedLevel = useSelector((state) => state.levelSelection.selectedLevel);
  const selectedHost = useSelector((state) => state.hostSelection.selectedHost);
  const [prefillLoading, setPrefillLoading] = useState(mode === 'edit');
  const [prefillError, setPrefillError] = useState('');
  const [originalGameData, setOriginalGameData] = useState(null);
  const prefillDoneRef = useRef(false);

  const handleAuthFailure = () => {
    clearStoredAdminSession();
    window.location.assign('/login');
  };

  useEffect(() => {
    prefillDoneRef.current = false;
  }, [mode, gameId]);

  useEffect(() => {
    if (stadiumsStatus === 'idle') {
      dispatch(loadStadiums());
    }
  }, [stadiumsStatus, dispatch]);

  useEffect(() => {
    if (mode !== 'edit') {
      setPrefillLoading(false);
      setPrefillError('');
      setOriginalGameData(null);
      prefillDoneRef.current = false;
      return;
    }

    if (!gameId) {
      setPrefillLoading(false);
      setPrefillError('Missing game ID for edit mode.');
      setOriginalGameData(null);
      return;
    }

    if (stadiumsStatus === 'loading' || stadiumsStatus === 'idle') {
      return;
    }

    if (stadiumsStatus === 'failed') {
      setPrefillLoading(false);
      setPrefillError('Failed to load stadium list.');
      setOriginalGameData(null);
      return;
    }

    if (prefillDoneRef.current) {
      return;
    }

    const hydrateForEdit = async () => {
      setPrefillLoading(true);
      setPrefillError('');
      try {
        const response = await runAdminAuthedRequest(
          (idToken) =>
            axios.get(`${process.env.REACT_APP_API_URL}/games/managed/${gameId}`, {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            }),
          {
            onAuthFailure: handleAuthFailure,
          }
        );
        if (!response) {
          return;
        }
        const game = response.data;

        const matchedStadium = findStadiumForGame(game, stadiums);
        if (!matchedStadium) {
          throw new Error('Could not match this game to an existing stadium.');
        }

        const calendarDate = toCalendarDateString(game?.date);
        if (!calendarDate) {
          throw new Error('Invalid game date.');
        }

        const matchedSlot = findSlotForGame(game, matchedStadium);
        if (!matchedSlot) {
          throw new Error('Could not match this game to an available slot.');
        }

        const matchedHost = findHostForGame(game, matchedStadium);

        dispatch(setSelectedStadium(matchedStadium));
        dispatch(setSelectedDate(calendarDate));
        dispatch(setSelectedSlot(matchedSlot));
        dispatch(setSelectedLevel(game?.type || null));
        dispatch(setSelectedHost(matchedHost));
        setOriginalGameData({
          stadiumId: matchedStadium?._id ? String(matchedStadium._id) : '',
          hostId: matchedHost?._id ? String(matchedHost._id) : '',
          date: calendarDate,
          time: game?.time || '',
          duration: Number(game?.duration) || 0,
          type: game?.type || '',
        });
        dispatch(resetPhase());

        prefillDoneRef.current = true;
      } catch (error) {
        const message = error?.response?.data?.error || error?.message || 'Failed to load game for editing.';
        setPrefillError(message);
      } finally {
        setPrefillLoading(false);
      }
    };

    hydrateForEdit();
  }, [mode, gameId, stadiumsStatus, stadiums, dispatch]);

  if (mode === 'edit' && prefillLoading) {
    return (
      <div className="create-game-layout full-width">
        <div className="create-game-content full-width">
          <LoadingOverlay label="Loading game editor" />
        </div>
      </div>
    );
  }

  if (mode === 'edit' && prefillError) {
    return (
      <div className="create-game-layout full-width">
        <div className="create-game-content full-width">
          <p>{prefillError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`create-game-layout ${phase === 1 || phase === 6 ? 'full-width' : ''}`}>
      {(prefillLoading || stadiumsStatus === 'loading') && (
        <LoadingOverlay
          label={prefillLoading ? 'Loading game editor' : 'Loading stadiums'}
        />
      )}
      {/* Display the background image container only for specific phases */}
      {phase !== 1 && phase !== 6 && (
        <div className="game-image-two-container">
          <img src={CreateGameImageTwo} alt="Game" className="game-image-two" />
          <div className="noiseeffect"></div>
        </div>
      )}

      <div
        className={`create-game-content ${phase === 1 || phase === 6 ? 'full-width' : ''} ${
          phase === 6 ? 'confirmation-phase' : ''
        }`}
      >
        {phase === 1 && <StadiumSelection stadiums={stadiums} />}
        {phase === 2 && <DateSelection />}
        {phase === 3 && <SlotSelection slots={selectedStadium?.slots || []} />}
        {phase === 4 && <LevelSelection />}
        {phase === 5 && <HostSelection />}
        {phase === 6 && (
          <Confirmation
            mode={mode}
            gameId={gameId}
            originalGameData={originalGameData}
            gameData={{
              stadium: selectedStadium,
              date: selectedDate,
              slot: selectedSlot,
              level: selectedLevel,
              host: selectedHost,
            }}
          />
        )}
      </div>
    </div>
  );
}

export default CreateGame;
